import React, {useState} from "react";
import {useParams} from "react-router-dom";
import {useAuthInfo} from "@propelauth/react";

import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogTitle,
    DialogTrigger,
} from "../../ui/dialog";
import {Button} from "../../ui/button";
import {Input} from "../../ui/input";
import {Textarea} from "../../ui/textarea";
import {postWithToken} from "../../../api/apiBase";
import {useToast} from "../../ui/use-toast";

type AddScopeDialogProps = {
    /** called after a successful create, so the caller can refresh its list */
    onCreated?: () => void;
};

/**
 * Create a scope without leaving the bid tab.
 *
 * Posts the same payload as the scopes list page - CreateScopeSchema wants
 * title and description, and description is required server-side even when
 * it is empty, so it is always sent.
 */
export default function AddScopeDialog({onCreated}: AddScopeDialogProps) {
    const {accessToken} = useAuthInfo();
    const {project_id} = useParams<string>();
    const {toast} = useToast();

    const [open, setOpen] = useState<boolean>(false);
    const [title, setTitle] = useState<string>("");
    const [description, setDescription] = useState<string>("");
    const [saving, setSaving] = useState<boolean>(false);

    const onCreate = () => {
        if (!accessToken || !title.trim()) return;
        setSaving(true);
        postWithToken(`/projects/${project_id}/scope`,
                      JSON.stringify({title: title.trim(), description: description}),
                      accessToken)
            .then(() => {
                toast({
                    title: "Success!",
                    description: "Scope created successfully",
                    variant: "default",
                });
                setOpen(false);
                setTitle("");
                setDescription("");
                if (onCreated) onCreated();
            })
            .catch((error) => {
                toast({
                    title: "Error",
                    description: error.message || "Failed to create scope.",
                    variant: "destructive",
                });
            })
            .finally(() => setSaving(false));
    };

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
                <Button variant="outline">Add Scope</Button>
            </DialogTrigger>
            <DialogContent>
                <DialogTitle>New Scope</DialogTitle>
                <DialogDescription>
                    Adds a scope to this project and opens it.
                </DialogDescription>

                <div className="space-y-3">
                    <div>
                        <label className="text-sm font-medium">Title</label>
                        <Input
                            value={title}
                            autoFocus
                            placeholder="e.g. 05 1000 Structural Steel"
                            onChange={(e) => setTitle(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === "Enter" && title.trim() && !saving) onCreate();
                            }}
                        />
                    </div>
                    <div>
                        <label className="text-sm font-medium">Description</label>
                        <Textarea
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                        />
                    </div>
                </div>

                <DialogFooter>
                    <Button variant="outline" onClick={() => setOpen(false)} disabled={saving}>
                        Cancel
                    </Button>
                    <Button onClick={onCreate} disabled={saving || !title.trim()}>
                        {saving ? "Creating..." : "Create"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
